// GoalsPanel — admin-only settings panel for monthly goals.
// Saves to /api/goals (POST). Goals are stored per-month as `goals:YYYY-MM` in settings collection.
import React, { useEffect, useState } from "react";
import { api, formatErr } from "./lib/api";

function currentMonthIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function GoalsPanel() {
  const [month, setMonth] = useState(currentMonthIso());
  const [revenue, setRevenue] = useState(0);
  // conversion59 was deprecated (Feb 2026) — the metric proved too noisy
  // because daily conversion depends on cup-stock and weekday traffic, not
  // just marketing. We keep it in the request payload so older stored goal
  // documents stay readable but no longer surface it in the UI.
  const [registration, setRegistration] = useState(0);
  const [avgInvoice, setAvgInvoice] = useState(0);
  const [msg, setMsg] = useState(null); // { type: "g"|"r", text }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/goals");
      if (data?.month) setMonth(data.month);
      setRevenue(data?.revenue || 0);
      setRegistration(data?.registration || 0);
      setAvgInvoice(data?.avg_invoice || 0);
    } catch (e) {
      setMsg({ type: "r", text: formatErr(e.response?.data?.detail) || e.message });
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setMsg(null); setSaving(true);
    try {
      await api.post("/goals", {
        month,
        revenue: Number(revenue) || 0,
        registration: Number(registration) || 0,
        avg_invoice: Number(avgInvoice) || 0,
      });
      setMsg({ type: "g", text: "✅ تم حفظ الأهداف" });
    } catch (e) {
      setMsg({ type: "r", text: formatErr(e.response?.data?.detail) || e.message });
    } finally { setSaving(false); }
  };

  const Field = ({ label, value, setValue, suffix, hint, testId }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12.5, color: "var(--brown)", fontWeight: 600 }}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="number"
          min="0"
          step="0.1"
          data-testid={testId}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ flex: 1, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13.5, fontFamily: "var(--f-n)" }}
        />
        <span style={{ fontSize: 12, color: "var(--muted)", minWidth: 30 }}>{suffix}</span>
      </div>
      {hint && <div style={{ fontSize: 11, color: "var(--muted)" }}>{hint}</div>}
    </div>
  );

  return (
    <div className="card" data-testid="goals-panel" style={{ marginTop: 14 }}>
      <h3 style={{ marginTop: 0, color: "var(--brown-deep)", fontSize: 16 }}>🎯 الأهداف الشهرية</h3>
      <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: -6, marginBottom: 14, lineHeight: 1.7 }}>
        حدّد أهداف الشهر الحالي. شريط التقدم يظهر تلقائياً في تبويب "عام" ويتحدّث مع كل رفع جديد.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <label style={{ fontSize: 12.5, color: "var(--brown)" }}>الشهر:</label>
        <input
          type="month"
          data-testid="goals-month-input"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13 }}
        />
        <button type="button" className="btn btn-soft" style={{ fontSize: 11.5 }} onClick={load} data-testid="goals-reload-btn">↺ تحديث</button>
      </div>

      <div className="g2" style={{ gap: 14 }}>
        <Field label="🏆 إيراد الشهر (ر)" value={revenue} setValue={setRevenue} suffix="ر" hint="مثال: 250,000 ر للشهر" testId="goal-revenue" />
        <Field label="📱 نسبة تسجيل العملاء (%)" value={registration} setValue={setRegistration} suffix="%" hint="هدف 80% فأكثر" testId="goal-reg" />
        <Field label="💵 متوسط الفاتورة (ر)" value={avgInvoice} setValue={setAvgInvoice} suffix="ر" hint="هدف 18 ر فأكثر" testId="goal-avg-inv" />
      </div>

      {msg && <div className={`al al${msg.type}`} style={{ marginTop: 12 }}>{msg.text}</div>}

      <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          type="button"
          className="btn btn-success"
          onClick={save}
          disabled={saving}
          data-testid="goals-save-btn"
        >
          {saving ? "⏳ جارٍ الحفظ..." : "💾 احفظ الأهداف"}
        </button>
      </div>
    </div>
  );
}
